import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import React, { memo } from 'react';
import styled from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';

interface Props {
	className?: string;
	children?: React.ReactNode;
	colorVariant?: 'success' | 'error' | 'warning' | 'pending';
	icon?: 'success' | 'remove' | 'void' | 'warning';
	onClick?: () => unknown;
}

const ICONS: Record<string, PredefinedIcons> = {
	success: 'checkmark-circle-contained-filled',
	remove: 'c-remove',
	void: 'radio-unchecked',
	warning: 'exclamation-circle',
};

const GenericStatus = ({ className, children, icon, onClick }: Props) => {
	return (
		<div className={className} onClick={onClick}>
			{icon ? (
				<>
					<Icon variant={ICONS[icon]} />
					&nbsp;
				</>
			) : null}
			{children}
		</div>
	);
};

const Memoized = memo(GenericStatus);
const Styled = styled(Memoized)`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.1em;
	min-width: 1rem;
	min-height: fit-content;
	padding: 0.3rem 0.9rem;
	letter-spacing: 1px;
	border-radius: 16px;
	text-transform: lowercase;

	span {
		::first-letter {
			text-transform: capitalize;
		}
	}
	color: white;
	font-weight: 400;
	font-size: 0.875rem;

	${({ colorVariant, theme }) => {
		switch (colorVariant) {
			case 'success':
				return `
                    background-color:  ${theme.v2.color.altLow.green};
                    color:  ${theme.v2.color.success[1200]};
                `;
			case 'warning':
				return `
                    background-color:  ${theme.v2.color.warning[600]};
                    color:  ${theme.v2.color.warning[1200]};
                `;
			case 'error':
				return `
                    background-color:  ${theme.v2.color.error[400]};
                    color:  ${theme.v2.color.error[1200]};
                `;
			case 'pending':
				return `
                    color:  ${theme.v2.color.neutral[900]};
                    background-color: ${theme.v2.color.neutral[300]};
                `;
			default:
				return `
                    color: #53585A;
                    border: 1px solid #CACED0;
                    background-color: #FFFFFF;
                `;
		}
	}}

	${({ icon }) => {
		switch (icon) {
			case 'success':
				return `
                    svg {
                        fill:  #FFFFFF;
                    }
                `;
			case 'remove':
				return `
                    svg {
                        fill:  #FFFFFF;
                    }
                `;
			case 'void':
				return `
                    svg {
                        fill: #53585A;
                    }
                `;
			default:
				return `
                `;
		}
	}}

    ${({ onClick }) => (onClick ? 'cursor: pointer;' : ``)}
`;

export { Styled as GenericStatus };
